"use client";

import {
  createMyUserProfile,
  getMyUser,
  updateMyUserProfile,
  updatePyloUser,
  type MeUser,
} from "@/services/me.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import posthog from "posthog-js";
import { checkIfAdminDashboardEntitiesExists } from "@/services/template.service";
import { useAuthenticatedQuery } from "@/hooks/use-authenticated-query";
import { useEntitiesFromDb } from "@/hooks/schema/use-entities-from-db";
import { useSetCustomerId } from "@/contexts/customer-id-context";

export const useMyUser = () => {
  const queryClient = useQueryClient();
  const setCustomerId = useSetCustomerId();
  const { entities } = useEntitiesFromDb();

  const adminExists = useMemo(() => checkIfAdminDashboardEntitiesExists(entities), [entities]);

  // Check if UserProfile entity exists and has all required fields
  const userProfileExists =
    adminExists?.templateResults?.["UserProfile"]?.entityExists &&
    adminExists.templateResults["UserProfile"]?.missingFields.length === 0 &&
    adminExists.templateResults["UserProfile"]?.missingRelations.length === 0;

  const { data: myUser, isLoading: isLoadingMyUser } = useAuthenticatedQuery({
    queryKey: ["myUser", !!userProfileExists],
    queryFn: () => getMyUser({ fetchUserProfile: !!userProfileExists }),
    // Wait for entities to load so we know if UserProfile exists
    enabled: entities !== undefined,
    // TODO: Invalidate on user changes from other sources (e.g., via websocket event)
    staleTime: 20 * 60 * 1000, // 20 minutes
  });

  useEffect(() => {
    if (myUser?.current_user?.data?.id) {
      const currentApp = myUser.current_user.data.pylo_app?.data;
      if (!currentApp?.pylo_customer?.data?.id) return;

      // Keep the customer-id context in sync. On normal loads SSR already seeds
      // this from the JWT (the single source of truth); this is a belt-and-braces
      // update for any client-only transition.
      setCustomerId(currentApp.pylo_customer.data.id);

      posthog.group("pylo_customer", currentApp.pylo_customer.data.id, {
        name: currentApp.pylo_customer.data.name,
      });

      posthog.identify(myUser.current_user.data.id, {
        email: myUser.current_user.data.email,
        pylo_customer_id: currentApp.pylo_customer.data.id,
        pylo_customer_name: currentApp.pylo_customer.data.name,
      });
    }
  }, [myUser, setCustomerId]);

  const { mutateAsync: createProfile } = useMutation({
    mutationFn: createMyUserProfile,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myUser"] });
    },
  });

  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } = useMutation({
    mutationFn: ({ firstname, lastname }: { firstname?: string; lastname?: string }) => {
      const currentUser = myUser?.current_user?.data;
      if (!currentUser?.user_profile?.data?.id) {
        throw new Error("No profile ID available");
      }

      return updateMyUserProfile({
        id: currentUser.user_profile.data.id,
        firstname,
        lastname,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myUser"] });
    },
  });

  const { mutateAsync: updateUser, isPending: isUpdatingUser } = useMutation({
    mutationFn: ({ email, password }: { email?: string; password?: string }) => {
      const currentUser = myUser?.current_user?.data;
      if (!currentUser?.id) {
        throw new Error("No user ID available");
      }
      return updatePyloUser({
        id: currentUser.id,
        email,
        password,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myUser"] });
    },
  });

  const { mutateAsync: upsertProfile, isPending: isUpsertingProfile } = useMutation({
    mutationFn: ({ firstname, lastname }: { firstname: string; lastname: string }) => {
      const currentUser = myUser?.current_user?.data;
      if (!currentUser?.user_profile?.data?.id) {
        return createProfile({ firstname, lastname });
      }
      return updateProfile({ firstname, lastname });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myUser"] });
    },
  });

  return {
    myUser,
    isLoadingMyUser,
    createProfile,
    updateProfile,
    upsertProfile,
    isUpsertingProfile,
    isUpdatingProfile,
    updateUser,
    isUpdatingUser,
  };
};
